import onetime from "@-/com.npmjs.onetime";

import manifestData from "#quizzes-manifest" with { type: "json" };
import type { QuizManifestEntry } from "#slide-anchor";

/**
 * The checked-in quiz manifest: every quiz's key + slug, the anchor that positions it, and its
 * answer key, in authored order.
 *
 * Generated with the slide manifest by `bun run gen` in this package (see
 * `-build-slides-manifest.ts`). Like the slide manifest, it keeps the runtime free of MDX parsing:
 * the lesson sequence, the review queue and grading read only this.
 */
export const getQuizManifest: () => QuizManifestEntry[] = onetime(() => manifestData);

const getQuizBySlug: () => Map<string, QuizManifestEntry> = onetime(
  () => new Map(getQuizManifest().map((entry) => [entry.slug, entry] as const)),
);

const getQuizByKey: () => Map<string, QuizManifestEntry> = onetime(
  () => new Map(getQuizManifest().map((entry) => [entry.key, entry] as const)),
);

/** Look up a quiz by URL slug, or `undefined` if the slug is not in the catalogue. */
export function getQuizManifestEntryBySlug(slug: string): QuizManifestEntry | undefined {
  return getQuizBySlug().get(slug);
}

/**
 * Look up a quiz by its UUID key, or `undefined` when no bundled quiz has it — a quiz since
 * retired, or one added by a newer build that synced its card here.
 */
export function getQuizManifestEntryByKey(key: string): QuizManifestEntry | undefined {
  return getQuizByKey().get(key);
}

/**
 * Whether `selected` answers the quiz: exactly its correct options, nothing more and nothing
 * less. Graded from the manifest's answer key, never from what was rendered.
 */
export function isQuizAnswerCorrect(
  quiz: Pick<QuizManifestEntry, "options">,
  selected: ReadonlySet<string>,
): boolean {
  return quiz.options.every((option) => option.correct === selected.has(option.glyph));
}
